import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Switch, FormControlLabel, TextField, IconButton, Menu, MenuItem } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Commented out static data - will be fetched from API
// const reporters = [
//   { id: 1, name: 'John Doe', phone: '1234567890', email: 'john@example.com', access: 'Reporter', status: 'Active' },
//   { id: 2, name: 'Jane Smith', phone: '9876543210', email: 'jane@example.com', access: 'Quality Manager', status: 'Active' },
//   { id: 3, name: 'Bob Wilson', phone: '5551234567', email: 'bob@example.com', access: 'Reporter', status: 'Inactive' },
//   { id: 4, name: 'Alice Brown', phone: '5559876543', email: 'alice@example.com', access: 'Reporter', status: 'Active' },
// ];

export default function ReporterList({ onAddReporter }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeToggle, setActiveToggle] = useState(true);
  const [reporters, setReporters] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReporter, setSelectedReporter] = useState(null);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Reporters:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch reporters data
  const fetchReporters = async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for reporters');
      setFetchingData(false);
      return;
    }

    setFetchingData(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_dashboard_admins', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 10,
          isActiveFilter: true,
          isActive: activeToggle,
          isRoleTypeFilter: true,
          roleType: 3, // Filter for Reporters only
          isSearchFilter: searchTerm.trim() !== "",
          search: searchTerm.trim()
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Reporters fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setReporters(result.data);
        } else {
          setReporters([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching reporters:', errorData);
        setReporters([]);
      }
    } catch (error) {
      console.error('Error fetching reporters:', error);
      setReporters([]);
    } finally {
      setFetchingData(false);
    }
  };

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchReporters();
  }, [searchTerm, activeToggle]);

  const filteredReporters = reporters;

  const handleMenuOpen = (event, reporter) => {
    setAnchorEl(event.currentTarget);
    setSelectedReporter(reporter);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReporter(null);
  };

  const handleEdit = () => {
    console.log('Edit reporter:', selectedReporter);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log('Delete reporter:', selectedReporter);
    handleMenuClose();
  };

  // Handle individual reporter status toggle
  const handleReporterStatusToggle = async (reporterId, currentStatus) => {
    try {
      // Get session data from localStorage
      const sessionId = localStorage.getItem('sessionId');
      const dashAdminId = localStorage.getItem('dashAdminId');
      
      if (!sessionId || !dashAdminId) {
        alert('Session data not found. Please login again.');
        return;
      }

      // Determine which endpoint to call based on current status
      const endpoint = currentStatus ? 'inactive_dashboard_admin' : 'active_dashboard_admin';
      const newStatus = !currentStatus;
      
      console.log(`🔄 Toggling reporter ${reporterId} from ${currentStatus} to ${newStatus} using ${endpoint}`);
      console.log(`🔄 User's dashAdminId: ${reporterId}`);
      console.log(`🔄 Current user's dashAdminId: ${dashAdminId}`);
      console.log(`🔄 selected_dashAdminId will be: ${reporterId}`);

      const requestBody = currentStatus 
        ? {
            sessionId,
            dashAdminId,
            selected_dashAdminId: reporterId
          }
        : {
            sessionId,
            dashAdminId,
            selected_dashAdminId: reporterId  // Pass the user's dashAdminId for activation too
          };

      const response = await fetch(`https://api.jpnewz.com/dashboard/${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log(`📥 ${endpoint} response:`, result);

      if (response.ok && result.status === true) {
        console.log(`✅ Reporter ${newStatus ? 'activated' : 'deactivated'} successfully`);
        
        // Refresh the reporters list to show the updated status
        await fetchReporters();
        
        // Update toggle state based on the new status
        console.log(`🔄 Current toggle state: ${activeToggle ? 'Active' : 'Inactive'}`);
        console.log(`🔄 Role type filter: 3 (Reporters)`);
        
        alert(`Reporter ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      } else {
        console.error(`❌ Failed to ${newStatus ? 'activate' : 'deactivate'} reporter:`, result.message);
        alert(`Error: ${result.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} reporter`}`);
      }
    } catch (error) {
      console.error('Error updating reporter status:', error);
      alert('Error updating reporter status. Please try again.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Reporter List
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddCircleOutlineIcon />}
          onClick={onAddReporter}
          sx={{ bgcolor: '#1976d2' }}
        >
          Add Reporter
        </Button>
      </Box>
      
      {/* Search and Filter Controls */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ color: '#666', fontWeight: 500 }}>Search</Typography>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '& fieldset': {
                  borderColor: '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={activeToggle}
              onChange={(e) => setActiveToggle(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
          }
          label={activeToggle ? "Active" : "Inactive"}
          sx={{ color: '#1976d2', fontWeight: 500 }}
        />
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetchingData ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Loading reporters...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredReporters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No reporters found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredReporters.map((reporter) => (
                <TableRow key={reporter.dashAdminId}>
                  <TableCell>{reporter.name}</TableCell>
                  <TableCell>{reporter.phone}</TableCell>
                  <TableCell>{reporter.email}</TableCell>
                  <TableCell>
                    <Switch
                      checked={reporter.isActive === true}
                      onChange={() => handleReporterStatusToggle(reporter.dashAdminId, reporter.isActive)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1976d2',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(event) => handleMenuOpen(event, reporter)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedReporter && selectedReporter.isActive === true ? (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        ) : (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
} 